import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"
import enewspaperApi from "../../libs/enewspaper"
import PaginationNew from "./components/PaginationNew"
import { formatThaiBuddhistDate } from "../../libs/dateUtils"
import { getStatusTextStyle } from "./utils/helpers"
import { COLORS } from "./constants/theme"
import { mainContainerStyle, headerSectionStyle, headerTitleStyle, thStyle, tdStyle, loadingStyle, emptyBoxStyle } from "./styles/commonStyles"
import {
	filterCardStyle,
	filterRowStyle,
	filterItemStyle,
	labelStyle,
	inputStyle,
	buttonGroupStyle,
	applyButtonStyle,
	exportButtonStyle,
	errorStyle,
	tableWrapperStyle,
	paymentTableStyle,
	tableCardStyle,
	dropdownTriggerStyle,
	dropdownSummaryContainerStyle,
	dropdownPlaceholderStyle,
	dropdownSingleValueStyle,
	dropdownMultiValueStyle,
	dropdownExtraCountStyle,
	dropdownMenuStyle,
	dropdownMenuHeaderStyle,
	dropdownMenuItemStyle,
	dropdownMenuLabelStyle,
	dropdownEmptyStateStyle,
} from "./styles/paymentTransactionStyles"
import {
	DEFAULT_PAGE_SIZE,
	PAYMENT_STATUS_OPTIONS,
	PAYMENT_METHOD_OPTIONS,
	INITIAL_FILTERS,
	RECURRING_TRUE_VALUES,
	RECURRING_FALSE_VALUES,
	getRecurringLabel,
	getPaymentStatusLabel,
	getPaymentMethodLabel,
	formatCsvCell,
	toSnakeTitleCase,
	getDefaultDateRange,
	getFirstNonEmptyValue,
	normalizeTransactionsResponse,
	formatAmount,
	normalizeMultiSelect,
	buildValueLabelMap,
	summarizeSelectionLabels,
} from "./utils/paymentTransactionUtils"

const DropdownSummary = ({ labels, placeholder }) => {
	const { visibleLabels, extraCount } = summarizeSelectionLabels(labels)

	if (labels.length === 0) {
		return <span style={dropdownPlaceholderStyle}>{placeholder}</span>
	}

	if (labels.length === 1) {
		return <span style={dropdownSingleValueStyle}>{labels[0]}</span>
	}

	return (
		<>
			<span style={dropdownMultiValueStyle} title={labels.join(", ")}>
				{visibleLabels.join(", ")}
			</span>
			{extraCount > 0 && <span style={dropdownExtraCountStyle}>+{extraCount} more</span>}
		</>
	)
}

const DropdownCaret = ({ open }) => (
	<span style={{ lineHeight: 1, transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>▼</span>
)

const PaymentTransaction = () => {
	const defaultRange = getDefaultDateRange()

	const [startDate, setStartDate] = useState(defaultRange.start)
	const [endDate, setEndDate] = useState(defaultRange.end)
	const [appliedRange, setAppliedRange] = useState(defaultRange)

	const [page, setPage] = useState(1)
	const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE)

	const [transactions, setTransactions] = useState([])
	const [packageOptions, setPackageOptions] = useState([])
	const [isPackageDropdownOpen, setIsPackageDropdownOpen] = useState(false)
	const packageDropdownRef = useRef(null)
	const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false)
	const statusDropdownRef = useRef(null)
	const [isPaymentMethodDropdownOpen, setIsPaymentMethodDropdownOpen] = useState(false)
	const paymentMethodDropdownRef = useRef(null)
	const [filters, setFilters] = useState(() => ({ ...INITIAL_FILTERS }))
	const [loading, setLoading] = useState(false)
	const [exporting, setExporting] = useState(false)
	const [error, setError] = useState("")

	const controllerRef = useRef(null)
	const limitRef = useRef(limit)

	const addPackageOptions = useCallback((values) => {
		const items = Array.isArray(values) ? values : [values]
		setPackageOptions((prevOptions) => {
			if (!items || items.length === 0) {
				return prevOptions
			}

			const existingValues = new Set(prevOptions.map((option) => option.value))
			const updatedOptions = [...prevOptions]

			items.forEach((value) => {
				if (value === undefined || value === null) {
					return
				}
				const normalizedValue = String(value).trim()
				if (!normalizedValue || existingValues.has(normalizedValue)) {
					return
				}
				existingValues.add(normalizedValue)
				updatedOptions.push({
					value: normalizedValue,
					label: normalizedValue,
				})
			})

			return updatedOptions
		})
	}, [])

	const fetchTransactions = useCallback(
		async (range) => {
			if (!range.start || !range.end) {
				return
			}

			if (controllerRef.current) {
				controllerRef.current.abort()
			}

			const controller = new AbortController()
			controllerRef.current = controller

			setLoading(true)
			setError("")

			try {
				const response = await enewspaperApi.getPaymentTransactions(
					{
						startDate: range.start,
						endDate: range.end,
					},
					{ signal: controller.signal }
				)

				if (response === false) {
					setError("ไม่สามารถโหลดข้อมูลได้")
					setTransactions([])
					return
				}

				const normalized = normalizeTransactionsResponse(response, limitRef.current || DEFAULT_PAGE_SIZE)

				setTransactions(normalized.items)
				const packageNames = normalized.items
					.map((transaction) => getFirstNonEmptyValue(transaction, ["packageName"]))
					.filter((name) => name !== undefined && name !== null && name !== "")
				addPackageOptions(packageNames)
			} catch (err) {
				if (err.name === "CanceledError" || err.name === "AbortError") {
					return
				}
				console.error("Failed to load payment transactions:", err)
				setError("เกิดข้อผิดพลาดในการโหลดข้อมูล")
				setTransactions([])
			} finally {
				setLoading(false)
			}
		},
		[addPackageOptions]
	)

	useEffect(() => {
		limitRef.current = limit
	}, [limit])

	useEffect(() => {
		fetchTransactions(appliedRange)
	}, [appliedRange, fetchTransactions])

	useEffect(() => {
		return () => controllerRef.current?.abort()
	}, [])

	useEffect(() => {
		const controller = new AbortController()

		const loadPackageOptions = async () => {
			try {
				const plans = await enewspaperApi.getPlanList({ signal: controller.signal })
				if (Array.isArray(plans)) {
					const planNames = plans
						.map((plan) => {
							if (!plan) return ""
							if (typeof plan === "string") {
								return plan
							}
							return plan.name || plan.planName || plan.title || ""
						})
						.filter((name) => typeof name === "string" && name.trim() !== "")
					addPackageOptions(planNames)
				}
			} catch (err) {
				if (err.name === "AbortError" || err.name === "CanceledError") {
					return
				}
				console.error("Failed to load plan list:", err)
			}
		}

		loadPackageOptions()

		return () => controller.abort()
	}, [addPackageOptions])

	useEffect(() => {
		const handleClickOutside = (event) => {
			const packageNode = packageDropdownRef.current
			const statusNode = statusDropdownRef.current
			const paymentMethodNode = paymentMethodDropdownRef.current
			const target = event.target

			if (packageNode && !packageNode.contains(target)) {
				setIsPackageDropdownOpen(false)
			}

			if (statusNode && !statusNode.contains(target)) {
				setIsStatusDropdownOpen(false)
			}

			if (paymentMethodNode && !paymentMethodNode.contains(target)) {
				setIsPaymentMethodDropdownOpen(false)
			}
		}

		document.addEventListener("mousedown", handleClickOutside)
		return () => document.removeEventListener("mousedown", handleClickOutside)
	}, [])

	const handleFilterChange = (field) => (event) => {
		const { value } = event.target
		setFilters((prevFilters) => ({
			...prevFilters,
			[field]: value,
		}))
	}

	const selectedStatusValues = normalizeMultiSelect(filters.paymentStatus)
	const selectedPaymentMethodValues = normalizeMultiSelect(filters.paymentMethod)

	const paymentStatusValueToLabel = useMemo(() => buildValueLabelMap(PAYMENT_STATUS_OPTIONS), [])
	const paymentMethodValueToLabel = useMemo(() => buildValueLabelMap(PAYMENT_METHOD_OPTIONS), [])

	const areAllStatusesSelected = useMemo(() => {
		if (PAYMENT_STATUS_OPTIONS.length === 0) {
			return false
		}
		return PAYMENT_STATUS_OPTIONS.every((option) => selectedStatusValues.includes(String(option.value)))
	}, [selectedStatusValues])

	const areAllPaymentMethodsSelected = useMemo(() => {
		if (PAYMENT_METHOD_OPTIONS.length === 0) {
			return false
		}
		return PAYMENT_METHOD_OPTIONS.every((option) => selectedPaymentMethodValues.includes(String(option.value)))
	}, [selectedPaymentMethodValues])

	const selectedStatusLabels = useMemo(() => {
		return selectedStatusValues
			.map((value) => paymentStatusValueToLabel.get(String(value)) || value)
			.filter((label) => label !== undefined && label !== null && label !== "")
	}, [selectedStatusValues, paymentStatusValueToLabel])

	const selectedPaymentMethodLabels = useMemo(() => {
		return selectedPaymentMethodValues
			.map((value) => paymentMethodValueToLabel.get(String(value)) || value)
			.filter((label) => label !== undefined && label !== null && label !== "")
	}, [selectedPaymentMethodValues, paymentMethodValueToLabel])

	const handleStatusToggle = useCallback((value) => {
		if (!value) {
			return
		}
		setFilters((prevFilters) => {
			const currentSelection = normalizeMultiSelect(prevFilters.paymentStatus)
			if (currentSelection.includes(value)) {
				return {
					...prevFilters,
					paymentStatus: currentSelection.filter((item) => item !== value),
				}
			}
			return {
				...prevFilters,
				paymentStatus: [...currentSelection, value],
			}
		})
	}, [])

	const handleStatusSelectAll = useCallback(() => {
		setFilters((prevFilters) => {
			const allValues = PAYMENT_STATUS_OPTIONS.map((option) => option.value)
			const shouldSelectAll = !areAllStatusesSelected
			return {
				...prevFilters,
				paymentStatus: shouldSelectAll ? allValues : [],
			}
		})
	}, [areAllStatusesSelected])

	const handlePaymentMethodToggle = useCallback((value) => {
		if (!value) {
			return
		}
		setFilters((prevFilters) => {
			const currentSelection = normalizeMultiSelect(prevFilters.paymentMethod)
			if (currentSelection.includes(value)) {
				return {
					...prevFilters,
					paymentMethod: currentSelection.filter((item) => item !== value),
				}
			}
			return {
				...prevFilters,
				paymentMethod: [...currentSelection, value],
			}
		})
	}, [])

	const handlePaymentMethodSelectAll = useCallback(() => {
		setFilters((prevFilters) => {
			const allValues = PAYMENT_METHOD_OPTIONS.map((option) => option.value)
			const shouldSelectAll = !areAllPaymentMethodsSelected
			return {
				...prevFilters,
				paymentMethod: shouldSelectAll ? allValues : [],
			}
		})
	}, [areAllPaymentMethodsSelected])

	const handlePackageToggle = useCallback((value) => {
		if (value === undefined || value === null) {
			return
		}
		const normalizedValue = String(value).trim()
		if (!normalizedValue) {
			return
		}
		setFilters((prevFilters) => {
			const currentSelection = normalizeMultiSelect(prevFilters.packageName)
			if (currentSelection.includes(normalizedValue)) {
				return {
					...prevFilters,
					packageName: currentSelection.filter((item) => item !== normalizedValue),
				}
			}
			return {
				...prevFilters,
				packageName: [...currentSelection, normalizedValue],
			}
		})
	}, [])

	const handlePackageSelectAll = useCallback(() => {
		setFilters((prevFilters) => {
			const currentSelection = normalizeMultiSelect(prevFilters.packageName)
			const allValues = packageOptions.map((option) => option.value)
			const isAllSelected = allValues.length > 0 && currentSelection.length === allValues.length
			return {
				...prevFilters,
				packageName: isAllSelected ? [] : allValues,
			}
		})
	}, [packageOptions])

	const sortedPackageOptions = useMemo(() => {
		return [...packageOptions].sort((a, b) => a.label.localeCompare(b.label, "th"))
	}, [packageOptions])

	const selectedPackageValues = normalizeMultiSelect(filters.packageName)
	const packageValueToLabel = useMemo(() => buildValueLabelMap(packageOptions), [packageOptions])

	const selectedPackageLabels = useMemo(() => {
		return selectedPackageValues
			.map((value) => {
				if (value === undefined || value === null) {
					return ""
				}
				const stringValue = String(value)
				return packageValueToLabel.get(stringValue) || stringValue
			})
			.filter((label) => label !== "")
	}, [selectedPackageValues, packageValueToLabel])

	const handlePackageDropdownKeyDown = useCallback((event) => {
		if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
			event.preventDefault()
			setIsPackageDropdownOpen((prev) => !prev)
		}
		if (event.key === "Escape") {
			event.preventDefault()
			setIsPackageDropdownOpen(false)
		}
	}, [])

	const handleStatusDropdownKeyDown = useCallback((event) => {
		if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
			event.preventDefault()
			setIsStatusDropdownOpen((prev) => !prev)
		}
		if (event.key === "Escape") {
			event.preventDefault()
			setIsStatusDropdownOpen(false)
		}
	}, [])

	const handlePaymentMethodDropdownKeyDown = useCallback((event) => {
		if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
			event.preventDefault()
			setIsPaymentMethodDropdownOpen((prev) => !prev)
		}
		if (event.key === "Escape") {
			event.preventDefault()
			setIsPaymentMethodDropdownOpen(false)
		}
	}, [])

	const handleApplyFilters = () => {
		if (!startDate || !endDate) {
			setError("กรุณาระบุวันที่ให้ครบถ้วน")
			return
		}

		const start = new Date(startDate)
		const end = new Date(endDate)
		const diffInMs = Math.abs(end - start)
		const diffInDays = diffInMs / (1000 * 60 * 60 * 24)

		if (startDate > endDate) {
			setError("วันเริ่มต้นต้องไม่น้อยกว่าวันสิ้นสุด")
			return
		}

		if (diffInDays > 31) {
			setError("ไม่สามารถเลือกช่วงวันที่เกิน 1 เดือนได้")
			return
		}

		setPage(1)
		setAppliedRange({ start: startDate, end: endDate })
		setIsPackageDropdownOpen(false)
		setIsStatusDropdownOpen(false)
		setIsPaymentMethodDropdownOpen(false)
	}

	const handlePageChange = (newPage) => {
		const nextPage = Math.min(Math.max(1, newPage), totalPages)
		setPage(nextPage)
	}

	const handleLimitChange = (newLimit) => {
		setLimit(newLimit > 0 ? newLimit : DEFAULT_PAGE_SIZE)
		setPage(1)
	}

	const handleExport = async () => {
		if (!appliedRange.start || !appliedRange.end) {
			setError("กรุณาระบุวันที่ให้ครบถ้วนก่อน Export")
			return
		}

		setExporting(true)
		setError("")
		try {
			if (filteredTransactions.length === 0) {
				throw new Error("empty_filtered_transactions")
			}

			const headerRow = columns.map((column) => formatCsvCell(column.label))
			const dataRows = filteredTransactions.map((transaction, index) => {
				return columns.map((column) => formatCsvCell(getColumnExportValue(transaction, column.key, index)))
			})

			const csvContent = [headerRow, ...dataRows].map((row) => row.join(",")).join("\n")
			const downloadBlob = new Blob([`\ufeff${csvContent}`], { type: "text/csv;charset=utf-8;" })

			const url = window.URL.createObjectURL(downloadBlob)
			const link = document.createElement("a")
			const fileName = `payment-transactions_${appliedRange.start}_to_${appliedRange.end}.csv`
			link.href = url
			link.setAttribute("download", fileName)
			document.body.appendChild(link)
			link.click()
			link.parentNode.removeChild(link)
			window.URL.revokeObjectURL(url)
		} catch (err) {
			if (err.message === "empty_filtered_transactions") {
				setError("ไม่พบข้อมูลตามตัวกรองที่เลือกสำหรับ Export")
			} else {
				console.error("Failed to export transactions:", err)
				setError("ไม่สามารถ Export ข้อมูลได้ กรุณาลองใหม่")
			}
		} finally {
			setExporting(false)
		}
	}

	const renderStatus = (statusValue) => {
		if (!statusValue) return ""
		const normalized = String(statusValue).toLowerCase()
		return <span style={getStatusTextStyle(normalized)}>{getPaymentStatusLabel(statusValue)}</span>
	}

	const renderAmount = (value) => {
		return formatAmount(value)
	}

	const renderText = (value, transform) => {
		if (value === undefined || value === null || value === "") return ""
		return transform ? transform(value) : value
	}

	const renderRecurring = (value) => {
		const label = getRecurringLabel(value)
		if (label === "Yes") {
			return (
				<span style={getStatusTextStyle("yes")} key="recurring-status">
					Yes
				</span>
			)
		}
		if (label === "No") {
			return (
				<span style={getStatusTextStyle("no")} key="recurring-status">
					No
				</span>
			)
		}
		return ""
	}

	const renderDateTime = (value, format = "YYYY-MM-DD HH:mm:ss") => {
		if (!value) return ""
		const formatted = formatThaiBuddhistDate(value, format)
		if (!formatted || formatted === "Invalid Date") {
			return ""
		}
		return formatted
	}

	const getColumnExportValue = (transaction, columnKey, rowIndex) => {
		const valueFrom = (paths) => getFirstNonEmptyValue(transaction, paths)

		switch (columnKey) {
			case "no":
				return rowIndex + 1
			case "date":
				return renderDateTime(valueFrom(["date"]))
			case "customerId":
				return valueFrom(["customerId"])
			case "packageName":
				return valueFrom(["packageName"])
			case "recurring":
				return valueFrom(["recurring"])
			case "previousPackageType":
				return valueFrom(["previousPackageType"])
			case "currentPackageType":
				return valueFrom(["currentPackageType"])
			case "startPackage":
				return renderDateTime(valueFrom(["startPackage"]))
			case "endPackage":
				return renderDateTime(valueFrom(["endPackage"]))
			case "price":
				return valueFrom(["price"])
			case "omiseFeeMax25Percent":
				return valueFrom(["omiseFeeMax25Percent"])
			case "omiseFeeVat7Percent":
				return valueFrom(["omiseFeeVat7Percent"])
			case "netRevenue":
				return valueFrom(["netRevenue"])
			case "paymentMethod":
				return valueFrom(["paymentMethod"])
			case "paymentStatus":
				return valueFrom(["paymentStatus"])
			case "failureCode":
				return valueFrom(["failureCode"])
			case "failureMessage":
				return valueFrom(["failureMessage"])
			case "successAt":
				return renderDateTime(valueFrom(["successAt"]))
			case "failedAt":
				return renderDateTime(valueFrom(["failedAt"]))
			case "expiredAt":
				return renderDateTime(valueFrom(["expiredAt"]))
			default:
				return valueFrom([columnKey])
		}
	}

	const filteredTransactions = useMemo(() => {
		if (!Array.isArray(transactions)) {
			return []
		}

		const normalizedFilters = {
			paymentStatuses: selectedStatusValues.map((value) => value.toLowerCase()),
			customerId: filters.customerId.trim().toLowerCase(),
			packageNames: selectedPackageValues.map((value) => value.toLowerCase()),
			paymentMethods: selectedPaymentMethodValues.map((value) => value.toLowerCase()),
			recurring: filters.recurring.trim().toLowerCase(),
		}

		return transactions.filter((transaction) => {
			const normalizedPaymentStatus = String(getFirstNonEmptyValue(transaction, ["paymentStatus"]) ?? "")
				.trim()
				.toLowerCase()
			const matchesPaymentStatus = normalizedFilters.paymentStatuses.length === 0 || normalizedFilters.paymentStatuses.includes(normalizedPaymentStatus)

			const matchesCustomerId =
				!normalizedFilters.customerId ||
				String(getFirstNonEmptyValue(transaction, ["customerId"]) ?? "")
					.toLowerCase()
					.includes(normalizedFilters.customerId)

			const normalizedPackageName = String(getFirstNonEmptyValue(transaction, ["packageName"]) ?? "")
				.trim()
				.toLowerCase()
			const matchesPackageName = normalizedFilters.packageNames.length === 0 || normalizedFilters.packageNames.includes(normalizedPackageName)

			const normalizedPaymentMethod = String(getFirstNonEmptyValue(transaction, ["paymentMethod"]) ?? "")
				.trim()
				.toLowerCase()
			const matchesPaymentMethod = normalizedFilters.paymentMethods.length === 0 || normalizedFilters.paymentMethods.includes(normalizedPaymentMethod)

			const normalizedRecurringValue = String(getFirstNonEmptyValue(transaction, ["recurring"]) ?? "").toLowerCase()
			const matchesRecurring =
				!normalizedFilters.recurring ||
				(normalizedFilters.recurring === "yes" && RECURRING_TRUE_VALUES.has(normalizedRecurringValue)) ||
				(normalizedFilters.recurring === "no" && RECURRING_FALSE_VALUES.has(normalizedRecurringValue))

			return matchesPaymentStatus && matchesCustomerId && matchesPackageName && matchesPaymentMethod && matchesRecurring
		})
	}, [transactions, filters, selectedStatusValues, selectedPackageValues, selectedPaymentMethodValues])

	const totalTransactions = filteredTransactions.length
	const effectiveLimit = limit > 0 ? limit : DEFAULT_PAGE_SIZE
	const totalPages = Math.max(1, Math.ceil(totalTransactions / effectiveLimit))
	const safePage = Math.min(page, totalPages)
	const startIndex = (safePage - 1) * effectiveLimit
	const endIndex = startIndex + effectiveLimit
	const paginatedTransactions = filteredTransactions.slice(startIndex, endIndex)

	const stickyColumnOrder = [
		{ key: "no", width: 50 },
		{ key: "date", width: 180 },
		{ key: "customerId", width: 120 },
		{ key: "packageName", width: 120 },
	]

	const stickyColumnsConfig = stickyColumnOrder.reduce((acc, column, index) => {
		const left = stickyColumnOrder.slice(0, index).reduce((sum, item) => sum + item.width, 0)
		acc[column.key] = {
			left,
			width: column.width,
		}
		return acc
	}, {})

	const getHeaderCellStyle = (column) => {
		const style = { ...thStyle }
		if (column.width) {
			style.width = column.width
			style.minWidth = column.width
		}
		if (column.minWidth) {
			style.minWidth = column.minWidth
		}
		if (column.align) {
			style.textAlign = column.align
		}
		style.whiteSpace = "nowrap"

		const stickyConfig = stickyColumnsConfig[column.key]
		if (stickyConfig) {
			style.position = "sticky"
			style.left = `${stickyConfig.left}px`
			style.zIndex = 3
			style.background = COLORS.gray50
			style.boxShadow = `inset -1px 0 0 ${COLORS.borderLight}`
		}
		return style
	}

	const getBodyCellStyle = (column) => {
		const style = { ...tdStyle }
		if (column.width) {
			style.width = column.width
			style.minWidth = column.width
		}
		if (column.minWidth) {
			style.minWidth = column.minWidth
		}
		if (column.align) {
			style.textAlign = column.align
		}
		const shouldWrap = column.allowWrap === true
		style.whiteSpace = shouldWrap ? "normal" : "nowrap"
		style.wordBreak = shouldWrap ? "break-word" : "normal"
		style.overflow = shouldWrap ? "visible" : "hidden"
		style.textOverflow = shouldWrap ? "clip" : "ellipsis"

		const stickyConfig = stickyColumnsConfig[column.key]
		if (stickyConfig) {
			style.position = "sticky"
			style.left = `${stickyConfig.left}px`
			style.zIndex = 2
			style.background = COLORS.white
			style.boxShadow = `inset -1px 0 0 ${COLORS.borderLight}`
		}

		return style
	}

	const columns = [
		{
			key: "no",
			label: "No.",
			width: stickyColumnsConfig.no?.width ?? 80,
			render: (transaction, rowIndex) => renderText(startIndex + rowIndex + 1),
		},
		{
			key: "date",
			label: "Date",
			width: stickyColumnsConfig.date?.width ?? 180,
			render: (transaction) => renderDateTime(getFirstNonEmptyValue(transaction, ["date"])),
		},
		{
			key: "customerId",
			label: "Customer ID",
			width: stickyColumnsConfig.customerId?.width ?? 200,
			render: (transaction) => renderText(getFirstNonEmptyValue(transaction, ["customerId"])),
		},
		{
			key: "packageName",
			label: "Package Name",
			minWidth: stickyColumnsConfig.packageName?.width ?? 200,
			allowWrap: true,
			render: (transaction) => renderText(getFirstNonEmptyValue(transaction, ["packageName"]), toSnakeTitleCase),
		},
		{
			key: "recurring",
			label: "Recurring",
			minWidth: 100,
			render: (transaction) => renderRecurring(getFirstNonEmptyValue(transaction, ["recurring"])),
		},
		{
			key: "previousPackageType",
			label: "Previous Package Type",
			minWidth: 120,
			allowWrap: true,
			render: (transaction) => renderText(getFirstNonEmptyValue(transaction, ["previousPackageType"]), toSnakeTitleCase),
		},
		{
			key: "currentPackageType",
			label: "Current Package Type",
			minWidth: 120,
			allowWrap: true,
			render: (transaction) => renderText(getFirstNonEmptyValue(transaction, ["currentPackageType"]), toSnakeTitleCase),
		},
		{
			key: "startPackage",
			label: "Start Package",
			minWidth: 180,
			render: (transaction) => renderDateTime(getFirstNonEmptyValue(transaction, ["startPackage"])),
		},
		{
			key: "endPackage",
			label: "End Package",
			minWidth: 180,
			render: (transaction) => renderDateTime(getFirstNonEmptyValue(transaction, ["endPackage"])),
		},
		{
			key: "price",
			label: "Price",
			minWidth: 120,
			align: "right",
			render: (transaction) => renderAmount(getFirstNonEmptyValue(transaction, ["price"])),
		},
		{
			key: "omiseFeeMax25Percent",
			label: "Omise Fee (Max 2.5%)",
			minWidth: 120,
			align: "right",
			render: (transaction) => renderAmount(getFirstNonEmptyValue(transaction, ["omiseFeeMax25Percent"])),
		},
		{
			key: "omiseFeeVat7Percent",
			label: "Omise Fee Vat  (7%)",
			minWidth: 120,
			align: "right",
			render: (transaction) => renderAmount(getFirstNonEmptyValue(transaction, ["omiseFeeVat7Percent"])),
		},
		{
			key: "netRevenue",
			label: "Net Revenue",
			minWidth: 120,
			align: "right",
			render: (transaction) => renderAmount(getFirstNonEmptyValue(transaction, ["netRevenue"])),
		},
		{
			key: "paymentMethod",
			label: "Payment Method",
			minWidth: 150,
			allowWrap: true,
			render: (transaction) => {
				const paymentMethod = getFirstNonEmptyValue(transaction, ["paymentMethod"])

				return <>{paymentMethod ? getPaymentMethodLabel(paymentMethod) : ""}</>
			},
		},
		{
			key: "paymentStatus",
			label: "Payment Status",
			minWidth: 120,
			allowWrap: true,
			render: (transaction) => renderStatus(getFirstNonEmptyValue(transaction, ["paymentStatus"])),
		},
		{
			key: "failureCode",
			label: "Failure Code",
			minWidth: 160,
			render: (transaction) => renderText(getFirstNonEmptyValue(transaction, ["failureCode"])),
		},
		{
			key: "failureMessage",
			label: "Failure Message",
			minWidth: 220,
			allowWrap: true,
			render: (transaction) => renderText(getFirstNonEmptyValue(transaction, ["failureMessage"])),
		},
		{
			key: "successAt",
			label: "Success At",
			minWidth: 180,
			render: (transaction) => renderDateTime(getFirstNonEmptyValue(transaction, ["successAt"])),
		},
		{
			key: "failedAt",
			label: "Failed At",
			minWidth: 180,
			render: (transaction) => renderDateTime(getFirstNonEmptyValue(transaction, ["failedAt"])),
		},
		{
			key: "expiredAt",
			label: "Expired At",
			minWidth: 180,
			render: (transaction) => renderDateTime(getFirstNonEmptyValue(transaction, ["expiredAt"])),
		},
	]

	useEffect(() => {
		if (page > totalPages) {
			setPage(totalPages)
		}
	}, [page, totalPages])

	return (
		<div style={mainContainerStyle}>
			<div style={headerSectionStyle}>
				<h2 style={headerTitleStyle}>Payment Transactions</h2>
			</div>

			<div style={filterCardStyle}>
				<div style={filterRowStyle}>
					<div style={filterItemStyle}>
						<label style={labelStyle} htmlFor="startDate">
							วันที่เริ่มต้น
						</label>
						<input id="startDate" type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} style={inputStyle} />
					</div>
					<div style={filterItemStyle}>
						<label style={labelStyle} htmlFor="endDate">
							วันที่สิ้นสุด
						</label>
						<input id="endDate" type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} style={inputStyle} />
					</div>
					<div style={{ flex: "0 0 auto" }}>
						<div style={buttonGroupStyle}>
							<button type="button" style={applyButtonStyle} onClick={handleApplyFilters}>
								ค้นหา
							</button>
							<button type="button" style={exportButtonStyle} onClick={handleExport} disabled={exporting || loading}>
								{exporting ? "กำลัง Export..." : "Export CSV"}
							</button>
						</div>
					</div>
				</div>
				<div style={filterRowStyle}>
					<div style={filterItemStyle}>
						<label style={labelStyle} htmlFor="customerId">
							Customer ID
						</label>
						<input
							id="customerId"
							type="text"
							value={filters.customerId}
							onChange={handleFilterChange("customerId")}
							style={inputStyle}
							placeholder="ค้นหาด้วย Customer ID"
						/>
					</div>
					<div style={filterItemStyle}>
						<label style={labelStyle} htmlFor="paymentStatusDropdown">
							Payment Status
						</label>
						<div style={{ position: "relative" }} ref={statusDropdownRef}>
							<div
								id="paymentStatusDropdown"
								role="button"
								tabIndex={0}
								onClick={() => setIsStatusDropdownOpen((prev) => !prev)}
								onKeyDown={handleStatusDropdownKeyDown}
								style={{
									...dropdownTriggerStyle,
									alignItems: selectedStatusLabels.length > 1 ? "flex-start" : "center",
								}}
							>
								<div style={dropdownSummaryContainerStyle}>
									<DropdownSummary labels={selectedStatusLabels} placeholder="สถานะทั้งหมด" />
								</div>
								<DropdownCaret open={isStatusDropdownOpen} />
							</div>
							{isStatusDropdownOpen && (
								<div style={dropdownMenuStyle}>
									<div style={dropdownMenuHeaderStyle}>
										<label style={{ ...dropdownMenuLabelStyle, fontWeight: 600 }}>
											<input type="checkbox" checked={areAllStatusesSelected} onChange={handleStatusSelectAll} />
											เลือกทั้งหมด
										</label>
									</div>
									{PAYMENT_STATUS_OPTIONS.map((option) => (
										<div key={option.value} style={dropdownMenuItemStyle}>
											<label style={dropdownMenuLabelStyle}>
												<input type="checkbox" checked={selectedStatusValues.includes(String(option.value))} onChange={() => handleStatusToggle(option.value)} />
												{option.label}
											</label>
										</div>
									))}
								</div>
							)}
						</div>
					</div>
					<div style={filterItemStyle}>
						<label style={labelStyle} htmlFor="packageNameDropdown">
							Package Name
						</label>
						<div style={{ position: "relative" }} ref={packageDropdownRef}>
							<div
								id="packageNameDropdown"
								role="button"
								tabIndex={0}
								onClick={() => setIsPackageDropdownOpen((prev) => !prev)}
								onKeyDown={handlePackageDropdownKeyDown}
								style={{
									...dropdownTriggerStyle,
									alignItems: selectedPackageLabels.length > 1 ? "flex-start" : "center",
								}}
							>
								<div style={dropdownSummaryContainerStyle}>
									<DropdownSummary labels={selectedPackageLabels} placeholder="แพ็คเกจทั้งหมด" />
								</div>
								<DropdownCaret open={isPackageDropdownOpen} />
							</div>
							{isPackageDropdownOpen && (
								<div style={dropdownMenuStyle}>
									<div style={dropdownMenuHeaderStyle}>
										<label style={{ ...dropdownMenuLabelStyle, fontWeight: 600 }}>
											<input
												type="checkbox"
												checked={sortedPackageOptions.length > 0 && selectedPackageValues.length === sortedPackageOptions.length}
												onChange={handlePackageSelectAll}
											/>
											เลือกทั้งหมด
										</label>
									</div>
									{sortedPackageOptions.length === 0 ? (
										<div style={dropdownEmptyStateStyle}>ไม่มีข้อมูลแพ็คเกจ</div>
									) : (
										sortedPackageOptions.map((option) => (
											<div key={option.value} style={dropdownMenuItemStyle}>
												<label style={dropdownMenuLabelStyle}>
													<input
														type="checkbox"
														checked={selectedPackageValues.includes(String(option.value))}
														onChange={() => handlePackageToggle(option.value)}
													/>
													{option.label}
												</label>
											</div>
										))
									)}
								</div>
							)}
						</div>
					</div>
					<div style={filterItemStyle}>
						<label style={labelStyle} htmlFor="paymentMethodDropdown">
							Payment Method
						</label>
						<div style={{ position: "relative" }} ref={paymentMethodDropdownRef}>
							<div
								id="paymentMethodDropdown"
								role="button"
								tabIndex={0}
								onClick={() => setIsPaymentMethodDropdownOpen((prev) => !prev)}
								onKeyDown={handlePaymentMethodDropdownKeyDown}
								style={{
									...dropdownTriggerStyle,
									alignItems: selectedPaymentMethodLabels.length > 1 ? "flex-start" : "center",
								}}
							>
								<div style={dropdownSummaryContainerStyle}>
									<DropdownSummary labels={selectedPaymentMethodLabels} placeholder="ช่องทางทั้งหมด" />
								</div>
								<DropdownCaret open={isPaymentMethodDropdownOpen} />
							</div>
							{isPaymentMethodDropdownOpen && (
								<div style={dropdownMenuStyle}>
									<div style={dropdownMenuHeaderStyle}>
										<label style={{ ...dropdownMenuLabelStyle, fontWeight: 600 }}>
											<input type="checkbox" checked={areAllPaymentMethodsSelected} onChange={handlePaymentMethodSelectAll} />
											เลือกทั้งหมด
										</label>
									</div>
									{PAYMENT_METHOD_OPTIONS.map((option) => (
										<div key={option.value} style={dropdownMenuItemStyle}>
											<label style={dropdownMenuLabelStyle}>
												<input
													type="checkbox"
													checked={selectedPaymentMethodValues.includes(String(option.value))}
													onChange={() => handlePaymentMethodToggle(option.value)}
												/>
												{option.label}
											</label>
										</div>
									))}
								</div>
							)}
						</div>
					</div>
					<div style={filterItemStyle}>
						<label style={labelStyle} htmlFor="recurring">
							Recurring
						</label>
						<select id="recurring" value={filters.recurring} onChange={handleFilterChange("recurring")} style={inputStyle}>
							<option value="">ทั้งหมด</option>
							<option value="yes">Yes</option>
							<option value="no">No</option>
						</select>
					</div>
				</div>

				{error && <div style={errorStyle}>{error}</div>}
			</div>

			<div style={tableCardStyle}>
				{loading ? (
					<div style={loadingStyle}>กำลังโหลด...</div>
				) : transactions.length === 0 ? (
					<div style={emptyBoxStyle}>ไม่พบข้อมูล</div>
				) : (
					<div style={tableWrapperStyle}>
						<table style={paymentTableStyle}>
							<thead>
								<tr>
									{columns.map((column) => (
										<th key={column.key} style={getHeaderCellStyle(column)}>
											{column.label}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{paginatedTransactions.map((transaction, rowIndex) => {
									return (
										<tr key={transaction.id || transaction.transactionId || rowIndex}>
											{columns.map((column) => (
												<td key={column.key} style={getBodyCellStyle(column)}>
													{column.render(transaction, rowIndex)}
												</td>
											))}
										</tr>
									)
								})}
							</tbody>
						</table>
					</div>
				)}
			</div>

			<PaginationNew
				page={safePage}
				totalPages={totalPages}
				total={totalTransactions}
				limit={limit}
				onPageChange={handlePageChange}
				onLimitChange={handleLimitChange}
			/>
		</div>
	)
}

export default PaymentTransaction
